import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Text,
  Pressable,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { Surface, Button, ActivityIndicator } from 'react-native-paper';
import HeaderBar from '../components/HeaderBar';
import MaxWidthContainer from '../components/MaxWidthContainer';
import helpService, { HelpRequest } from '../services/helpService';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import SlidingMenu from '../components/SlidingMenu';
import { RootStackParamList } from '../types/navigation';
import { brandPrimary, brandPrimarySoft, brandButton } from '../theme/paperTheme';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const FILTERS = [
  { key: 'all' as const, label: 'All' },
  { key: 'pending' as const, label: 'Unanswered' },
  { key: 'answered' as const, label: 'Answered' },
];

const AllHelpTicketsScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;

  const [helpRequests, setHelpRequests] = useState<HelpRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<'all' | 'pending' | 'answered'>('all');
  const [isMenuVisible, setIsMenuVisible] = useState(false);

  const toggleMenu = () => setIsMenuVisible((v) => !v);

  useEffect(() => {
    fetchHelpRequests();
  }, [activeFilter]);

  const fetchHelpRequests = async () => {
    setIsLoading(true);
    try {
      const requests = await helpService.getHelpRequests(activeFilter);
      setHelpRequests(requests);
    } catch (error) {
      setHelpRequests([]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderHelpRequest = ({ item }: { item: HelpRequest }) => (
    <Surface style={styles.helpRequestCard} elevation={1}>
      <Text style={styles.helpRequestTitle}>{item.title}</Text>
      {item.category ? (
        <Text style={styles.helpRequestCategory}>{item.category}</Text>
      ) : null}
      <Text style={styles.helpRequestDescription}>{item.description}</Text>
      <View style={styles.helpRequestMeta}>
        <Text style={styles.helpRequestAuthor}>{item.user?.displayName ?? 'Teacher'}</Text>
        <Text style={styles.helpRequestDate}>
          {new Date(item.createdAt).toLocaleDateString()}
        </Text>
      </View>
      <View style={styles.cardFooter}>
        <Button
          mode="contained"
          style={styles.respondButton}
          buttonColor={brandButton}
          textColor="#ffffff"
          onPress={() => navigation.navigate('HelpResponse', { helpRequest: item })}
        >
          Respond
        </Button>
      </View>
    </Surface>
  );

  return (
    <View style={styles.container}>
      <Surface style={styles.surface}>
        <View style={styles.headerWrap}>
          <HeaderBar
            style={styles.headerContainer}
            rightContent={
              <TouchableOpacity style={styles.menuButton} onPress={toggleMenu}>
                <Text style={styles.menuText}>☰</Text>
              </TouchableOpacity>
            }
            onLogoPress={() => navigation.navigate('Home')}
          />
        </View>

        <MaxWidthContainer style={isDesktop ? styles.wideContainer : undefined}>
          <FlatList
            data={helpRequests}
            renderItem={renderHelpRequest}
            keyExtractor={(item) => item._id}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.listContent}
            ListHeaderComponent={
              <>
                <View style={styles.heroSection}>
                  <Text style={styles.headline}>Ready to lend a hand?</Text>
                  <Text style={styles.subheading}>
                    Browse support requests and respond when you can help.
                  </Text>
                </View>

                <View style={styles.toggleButtonRow}>
                  <Pressable
                    style={styles.toggleButton}
                    onPress={() => navigation.navigate('Home')}
                  >
                    <Text style={styles.toggleButtonText}>Ask for Help</Text>
                  </Pressable>
                  <Pressable style={[styles.toggleButton, styles.toggleButtonActive]}>
                    <Text style={[styles.toggleButtonText, styles.toggleButtonTextActive]}>
                      Offer Help
                    </Text>
                  </Pressable>
                </View>

                <View style={styles.filterContainer}>
                  {FILTERS.map((filter) => (
                    <TouchableOpacity
                      key={filter.key}
                      style={[
                        styles.filterPill,
                        activeFilter === filter.key && styles.filterPillActive,
                      ]}
                      onPress={() => setActiveFilter(filter.key)}
                    >
                      <Text
                        style={[
                          styles.filterText,
                          activeFilter === filter.key && styles.filterTextActive,
                        ]}
                      >
                        {filter.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                {isLoading ? (
                  <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color={brandPrimary} />
                    <Text style={styles.loadingText}>Loading help requests…</Text>
                  </View>
                ) : null}
              </>
            }
            ListEmptyComponent={
              !isLoading ? (
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyEmoji}>🌿</Text>
                  <Text style={styles.emptyTitle}>No help tickets found</Text>
                  <Text style={styles.emptyText}>
                    Try another filter, or check back soon for new requests.
                  </Text>
                </View>
              ) : null
            }
          />
        </MaxWidthContainer>
      </Surface>
      <SlidingMenu isVisible={isMenuVisible} onClose={toggleMenu} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F4F7FB',
  },
  surface: {
    flex: 1,
    elevation: 0,
    backgroundColor: '#F4F7FB',
  },
  wideContainer: {
    maxWidth: 960,
  },
  headerWrap: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 0 rgba(15, 23, 42, 0.06)' }
      : {
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(15, 23, 42, 0.06)',
        }),
  },
  headerContainer: {
    marginHorizontal: 8,
    marginBottom: 0,
  },
  heroSection: {
    marginBottom: 24,
    paddingTop: 28,
  },
  headline: {
    fontSize: 32,
    fontWeight: '700',
    color: '#1E293B',
    letterSpacing: -0.5,
    marginBottom: 10,
    lineHeight: 40,
  },
  subheading: {
    fontSize: 17,
    color: '#64748B',
    lineHeight: 26,
  },
  toggleButtonRow: {
    flexDirection: 'row',
    width: '100%',
    marginBottom: 24,
    backgroundColor: '#FFFFFF',
    borderRadius: 999,
    padding: 4,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.08)',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 12px rgba(15, 23, 42, 0.04)' }
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.04,
          shadowRadius: 8,
          elevation: 1,
        }),
  },
  toggleButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleButtonActive: {
    backgroundColor: brandButton,
  },
  toggleButtonText: {
    color: '#64748B',
    fontWeight: '600',
    fontSize: 15,
  },
  toggleButtonTextActive: {
    color: '#FFFFFF',
  },
  filterContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 20,
  },
  filterPill: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.08)',
  },
  filterPillActive: {
    backgroundColor: brandButton,
    borderColor: brandButton,
  },
  filterText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  filterTextActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  listContent: {
    paddingHorizontal: 20,
    paddingBottom: 48,
    gap: 14,
  },
  helpRequestCard: {
    backgroundColor: '#FFFFFF',
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    marginBottom: 0,
  },
  helpRequestTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
    lineHeight: 26,
  },
  helpRequestCategory: {
    fontSize: 12,
    fontWeight: '600',
    color: brandPrimary,
    backgroundColor: brandPrimarySoft,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    alignSelf: 'flex-start',
    marginBottom: 10,
    overflow: 'hidden',
  },
  helpRequestDescription: {
    fontSize: 15,
    color: '#475569',
    lineHeight: 22,
    marginBottom: 14,
  },
  helpRequestMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  helpRequestAuthor: {
    fontSize: 13,
    color: '#64748B',
    fontWeight: '500',
  },
  helpRequestDate: {
    fontSize: 13,
    color: '#94A3B8',
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  respondButton: {
    borderRadius: 999,
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 15,
    color: '#64748B',
  },
  emptyContainer: {
    alignItems: 'center',
    padding: 32,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    marginTop: 8,
  },
  emptyEmoji: {
    fontSize: 36,
    marginBottom: 10,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyText: {
    textAlign: 'center',
    color: '#64748B',
    fontSize: 15,
    lineHeight: 22,
    maxWidth: 300,
  },
  menuButton: {
    width: 52,
    height: 52,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuText: {
    fontSize: 36,
    color: '#1E293B',
  },
});

export default AllHelpTicketsScreen;
